import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, PhoneCall, ChevronDown, ChevronUp, Building2, Layers, 
  Activity, Sparkles, MapPin, LayoutGrid, Home, Info, Phone 
} from 'lucide-react';
import { useLocale } from '../context/LocaleContext';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSelector from '../theme/ThemeSelector';
import { FunctionButton } from './motion/FunctionButton';
import { easings, durations } from './motion/motionTokens';

const PremiumHeader = () => {
  const { t, isRtl } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch('/api/v1/settings')
      .then((res) => res.json())
      .then((data) => setSettings(data))
      .catch(() => setSettings(null));
  }, []);

  const projectLinks = [
    { path: '/projects', label: isRtl ? 'تمام منصوبے' : 'All Projects' },
    { path: '/projects/prime-mall', label: 'Prime Mall (Ongoing)' },
    { path: '/projects/prime-arcade', label: 'Prime Arcade (Delivered)' },
  ];

  const navItems = [
    { path: '/', label: 'nav.home' },
    { path: '/projects', label: 'nav.projects', children: projectLinks },
    { path: '/investment', label: 'nav.investment' },
    { path: '/gallery', label: 'nav.gallery' },
    { path: '/updates', label: 'nav.updates' },
    { path: '/about', label: 'nav.about' },
    { path: '/contact', label: 'nav.contact' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-theme-page/95 backdrop-blur-md py-3 shadow-theme border-b border-theme' 
          : 'bg-theme-page/75 backdrop-blur-[6px] py-4 md:py-5 border-b border-theme/40'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between">
        
        {/* LOGO with 5% scroll shrink transition */}
        <Link to="/" className="flex items-center gap-2.5 sm:gap-3.5 group max-w-[70%] sm:max-w-none">
          <motion.span 
            animate={{ scale: scrolled ? 0.95 : 1 }}
            transition={{ duration: durations.interface, ease: easings.entrance }}
            className="relative flex h-10 w-12 sm:h-11 sm:w-13 shrink-0 items-center justify-center overflow-hidden rounded-[4px] border border-theme-gold/45 bg-[#F2EEE6] p-1 shadow-md ring-1 ring-white/10 transition-all duration-300 group-hover:border-theme-gold"
          >
            <span className="absolute inset-x-1 top-1 h-px bg-theme-gold/35" />
            {settings?.logo ? (
              <img
                src={settings.logo}
                alt={t('nav.home') === 'ہوم' ? settings.company_name_ur : settings.company_name_en}
                className="relative z-10 max-h-full max-w-full object-contain mix-blend-multiply"
              />
            ) : (
              <span className="relative z-10 text-[#0B1118] font-bold text-base sm:text-lg font-headingEn tracking-widest">PBZ</span>
            )}
          </motion.span>
          <span className="flex flex-col leading-tight border-l border-theme-gold/30 pl-2.5 sm:pl-3 truncate">
            <span className="text-theme-heading font-bold text-sm sm:text-base md:text-[18px] tracking-[0.02em] group-hover:text-theme-gold transition-colors duration-300 truncate">
              {t('nav.home') === 'ہوم' ? (settings?.company_name_ur || 'پرائم بزنس زون') : (settings?.company_name_en || 'Prime Business Zone')}
            </span>
            <span className="hidden sm:block mt-0.5 text-[8px] sm:text-[9px] uppercase tracking-[0.35em] text-theme-gold font-mono">
              Commercial Development
            </span>
          </span>
        </Link>

        {/* DESKTOP NAV LINKS WITH SHARED-LAYOUT UNDERLINE */}
        <nav className="hidden lg:flex items-center gap-3.5 xl:gap-5 2xl:gap-7 shrink-0">
          {navItems.map((item) => {
            const isProjects = item.path === '/projects';
            const isActive = isProjects 
              ? location.pathname.startsWith('/projects') 
              : item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);

            return item.children ? (
              <div 
                key={item.path} 
                className="relative shrink-0"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <NavLink
                  to={item.path}
                  className={`relative py-1 text-xs font-bold uppercase tracking-wider transition-colors duration-200 flex items-center gap-1 whitespace-nowrap shrink-0 ${
                    isActive ? 'text-theme-gold font-extrabold' : 'text-theme-primary/80 hover:text-theme-gold'
                  }`}
                >
                  <span className="whitespace-nowrap">{item.raw ? item.label : t(item.label)}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 shrink-0 ${dropdownOpen ? 'rotate-180 text-theme-gold' : 'text-theme-muted'}`} />

                  {/* Shared-Layout Glide Underline */}
                  {isActive && (
                    <motion.div
                      layoutId="active-nav-indicator"
                      className="absolute left-0 bottom-0 right-0 h-[2px] bg-theme-gold shadow-[0_0_8px_rgba(185,154,91,0.6)]"
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                </NavLink>

                {/* Animated Dropdown */}
                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.98 }}
                      transition={{ duration: 0.2, ease: easings.entrance }}
                      className="absolute left-0 top-full pt-2 min-w-[210px] z-50 pointer-events-auto"
                    >
                      <div className="border border-theme bg-theme-surface p-2 rounded-xl shadow-2xl backdrop-blur-xl">
                        {item.children.map((child, idx) => (
                          <motion.div
                            key={child.path}
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.15, delay: idx * 0.04 }}
                          >
                            <Link
                              to={child.path}
                              onClick={() => setDropdownOpen(false)}
                              className="block px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-theme-primary/80 hover:bg-theme-page hover:text-theme-gold transition-colors whitespace-nowrap"
                            >
                              {child.label}
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive: linkActive }) =>
                  `relative py-1 text-xs font-bold uppercase tracking-wider transition-colors duration-200 whitespace-nowrap shrink-0 ${
                    linkActive ? 'text-theme-gold font-extrabold' : 'text-theme-primary/80 hover:text-theme-gold'
                  }`
                }
              >
                {({ isActive: linkActive }) => (
                  <>
                    <span className="whitespace-nowrap">{item.raw ? item.label : t(item.label)}</span>
                    {linkActive && (
                      <motion.div
                        layoutId="active-nav-indicator"
                        className="absolute left-0 bottom-0 right-0 h-[2px] bg-theme-gold shadow-[0_0_8px_rgba(185,154,91,0.6)]"
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* DESKTOP ACTIONS */}
        <div className="hidden lg:flex items-center gap-4">
          <ThemeSelector />
          <LanguageSwitcher />

          <FunctionButton
            variant="explore-availability"
            to="/investment"
            className="!py-2.5 !px-5"
          >
            {isRtl ? 'دستیابی دیکھیں' : 'Explore Availability'}
          </FunctionButton>
        </div>

        {/* MOBILE TOGGLE BUTTON (Morphing Hamburger -> X) */}
        <div className="flex items-center gap-2.5 sm:gap-4 lg:hidden">
          <ThemeSelector />
          <LanguageSwitcher />
          
          <motion.button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.92 }}
            className="text-theme-primary p-2 hover:text-theme-gold transition-colors rounded-xl bg-theme-surface border border-theme shadow-md cursor-pointer"
            aria-expanded={isOpen}
            aria-label="Toggle mobile menu"
          >
            <motion.div
              animate={{ rotate: isOpen ? 90 : 0 }}
              transition={{ duration: 0.25 }}
            >
              {isOpen ? <X className="w-6 h-6 text-theme-gold" /> : <Menu className="w-6 h-6 text-theme-gold" />}
            </motion.div>
          </motion.button>
        </div>
      </div>

      {/* MOBILE FULLSCREEN OVERLAY DRAWER WITH STAGGERED LINKS */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3, ease: easings.entrance }}
            className="fixed inset-0 z-[100] bg-theme-page text-theme-primary w-full h-full min-h-[100dvh] overflow-y-auto overscroll-contain lg:hidden font-sans flex flex-col justify-between px-5 py-6 sm:px-8"
          >
            {/* Top Bar inside Drawer */}
            <div className="flex items-center justify-between pb-4 border-b border-theme shrink-0">
              <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-3">
                <span className="flex h-10 w-12 items-center justify-center rounded-[4px] border border-theme-gold/50 bg-[#F2EEE6] p-1 shadow-md">
                  {settings?.logo ? (
                    <img
                      src={settings.logo}
                      alt="PBZ Logo"
                      className="max-h-full max-w-full object-contain mix-blend-multiply"
                    />
                  ) : (
                    <span className="text-[#0B1118] font-bold text-sm font-headingEn">PBZ</span>
                  )}
                </span>
                <span className="text-theme-heading font-bold text-base tracking-wider">
                  {t('nav.home') === 'ہوم' ? (settings?.company_name_ur || 'پرائم بزنس زون') : (settings?.company_name_en || 'Prime Business Zone')}
                </span>
              </Link>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-theme-gold p-2.5 border border-theme rounded-xl bg-theme-surface active:scale-95 transition-transform cursor-pointer"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Quick Action Matrix inside Mobile Menu */}
            <div className="py-4 border-b border-theme shrink-0">
              <span className="text-[10px] uppercase font-mono tracking-widest text-theme-gold block mb-3 font-bold">
                {isRtl ? 'فوری رسائی' : 'DIRECT ACCESS'}
              </span>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { path: '/projects/prime-mall', label: 'Prime Mall', badge: 'Active', icon: Building2 },
                  { path: '/projects/prime-arcade', label: 'Prime Arcade', badge: 'Delivered', icon: Layers },
                  { path: '/updates', label: 'Updates', icon: Activity },
                  { path: '/gallery', label: 'Gallery', icon: Sparkles },
                ].map((item, idx) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: 0.1 + idx * 0.04 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-2.5 p-2.5 rounded-xl border text-xs font-bold transition-all ${
                        location.pathname === item.path
                          ? 'bg-theme-surface border-theme-gold text-theme-gold'
                          : 'bg-theme-surface border-theme text-theme-primary hover:border-theme-gold'
                      }`}
                    >
                      <item.icon className="w-4 h-4 text-theme-gold shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{item.label}</span>
                        {item.badge && <span className="text-[9px] font-mono text-theme-gold uppercase">{item.badge}</span>}
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Nav Menu List */}
            <div className="py-4 flex flex-col gap-2">
              {navItems.map((item, idx) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: 0.15 + idx * 0.04 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-between py-2 text-base font-bold uppercase tracking-wider text-theme-primary hover:text-theme-gold"
                  >
                    <span>{item.raw ? item.label : t(item.label)}</span>
                    <span className="text-xs text-theme-gold font-mono">0{idx + 1}</span>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Mobile Theme Selector */}
            <div className="py-2 shrink-0">
              <ThemeSelector isMobile={true} />
            </div>

            {/* Bottom CTA in Drawer */}
            <div className="pt-3 border-t border-theme shrink-0">
              <FunctionButton
                variant="explore-availability"
                to="/investment"
                onClick={() => setIsOpen(false)}
                className="w-full justify-center !py-3.5"
              >
                {isRtl ? 'دستیابی دیکھیں' : 'Explore Availability'}
              </FunctionButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default PremiumHeader;
